import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

// ── className helper ──────────────────────────────────────────────────────────
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ── Currency formatting ───────────────────────────────────────────────────────
export function fmt(value: number, decimals = 0): string {
  if (isNaN(value) || !isFinite(value)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function fmtShort(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000)     return `$${(value / 1_000).toFixed(1)}K`;
  return fmt(value);
}

export function fmtNumber(value: number, decimals = 2): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function fmtPercent(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

// ── Date formatting ───────────────────────────────────────────────────────────
export function fmtDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  }).format(new Date(iso));
}

export function fmtDateTime(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso));
}

// ── Truncate ──────────────────────────────────────────────────────────────────
export function truncate(str: string, maxLength: number): string {
  return str.length > maxLength ? str.slice(0, maxLength - 1) + '…' : str;
}

// ── Asset type label ──────────────────────────────────────────────────────────
export const ASSET_TYPE_LABELS: Record<string, string> = {
  hvac: 'HVAC System',
  electrical: 'Electrical',
  plumbing: 'Plumbing & Water',
  structural: 'Structural',
  envelope: 'Building Envelope',
  elevator: 'Elevator / Lift',
  lighting: 'Lighting System',
  fire_safety: 'Fire Safety',
  other: 'Other',
};

// ── Color palette for charts ──────────────────────────────────────────────────
export const CHART_COLORS = {
  navy:   '#1b3a5f',
  amber:  '#c8882a',
  teal:   '#1a7a6e',
  red:    '#b23a38',
  blue:   '#2d64a5',
  purple: '#6d4c9e',
  green:  '#2e7d52',
  orange: '#c85a1a',
};

export const CHART_PALETTE = Object.values(CHART_COLORS);

// ── CSV export ────────────────────────────────────────────────────────────────
export function downloadCSV(filename: string, rows: string[][]): void {
  const csvContent = rows.map(row =>
    row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
  ).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href     = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
